import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setManagerSession, formatDetail } from "../lib/api";
import BrandMark from "../components/BrandMark";
import { ArrowLeft } from "lucide-react";

export default function ControlLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/auth/manager/login", { email, pin });
      setManagerSession(r.data.token, r.data.manager);
      nav("/control/dashboard");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (which) => {
    if (which === "vikram") { setEmail("vikram@beno.com"); setPin("2468"); }
    else { setEmail("sonia@beno.com"); setPin("1357"); }
  };

  return (
    <div className="min-h-screen grid grid-cols-12 bg-white">
      <div className="col-span-12 lg:col-span-5 p-10 md:p-16 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mono-label w-fit" data-testid="login-back">
          <ArrowLeft size={14} /> Back to start
        </Link>
        <div className="flex-1 flex flex-col justify-center max-w-md">
          <BrandMark size={34} />
          <div className="mono-label mt-6">Control Room</div>
          <h1 className="font-heading text-5xl font-black tracking-tighter mt-2">Manager login.</h1>
          <p className="mt-3 text-neutral-600">Enter your email and 4-digit PIN. Short-lived JWT (8h).</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mono-label">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 h-14 px-4 border border-neutral-300 bg-white font-mono focus:outline-none focus:border-black"
                placeholder="vikram@beno.com"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="mono-label">PIN</label>
              <input
                data-testid="login-pin"
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                className="w-full mt-2 h-14 px-4 border border-neutral-300 bg-white font-mono tracking-[0.5em] text-lg focus:outline-none focus:border-black"
                placeholder="••••"
                autoComplete="current-password"
              />
            </div>
            {error && <div data-testid="login-error" className="text-red-700 font-mono text-sm">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-black text-white font-mono uppercase tracking-wider text-sm hover:bg-neutral-800 disabled:opacity-50"
            >
              {loading ? "Authenticating…" : "Enter Control Room"}
            </button>
          </form>
          <div className="mt-8 border-t border-neutral-200 pt-6">
            <div className="mono-label">Demo accounts</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="demo-vikram" type="button" onClick={() => fillDemo("vikram")} className="h-10 px-3 border border-neutral-300 font-mono text-xs hover:bg-neutral-100">
                vikram@beno.com · 2468
              </button>
              <button data-testid="demo-sonia" type="button" onClick={() => fillDemo("sonia")} className="h-10 px-3 border border-neutral-300 font-mono text-xs hover:bg-neutral-100">
                sonia@beno.com · 1357
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:flex col-span-7 bg-black text-white p-16 flex-col justify-between relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/6f1a01bc-6d96-4288-a648-39b8d288a185/images/f8d7dcdd397b03291e88d98b4799aacb83783daf4f3647970ccdc566247b2a0b.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative">
          <div className="mono-label text-neutral-400">NON-NEGOTIABLE</div>
          <h2 className="font-heading text-5xl font-black tracking-tighter leading-[0.95] mt-3 max-w-xl">
            Terminal captures.<br/>Engine decides.<br/>Room governs.
          </h2>
          <p className="mt-4 max-w-md text-neutral-400">
            Control Room observes, scores and escalates. It never mutates attendance directly.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-4 font-mono text-xs">
          <div><div className="mono-label text-neutral-500">HIGH</div><div className="text-[#22C55E]">≥ 0.90 · auto-mark</div></div>
          <div><div className="mono-label text-neutral-500">MEDIUM</div><div className="text-[#EAB308]">0.75–0.89 · review</div></div>
          <div><div className="mono-label text-neutral-500">LOW</div><div className="text-[#EF4444]">&lt; 0.75 · retry/override</div></div>
        </div>
      </div>
    </div>
  );
}
